import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaExclamationTriangle, FaHome, FaRocket } from "react-icons/fa";
import { useState, useEffect, useCallback } from "react";

const SpaceExplorerGame = () => {
  const [shipPosition, setShipPosition] = useState({ x: 150, y: 150 });
  const [debris, setDebris] = useState([]);
  const [energy, setEnergy] = useState(100);
  const [gameActive, setGameActive] = useState(true);
  const [message, setMessage] = useState("Navigate through space debris!");

  const gameWidth = 300;
  const gameHeight = 200;

  // Generate space debris
  const generateDebris = useCallback(() => {
    if (!gameActive || debris.length >= 5) return;

    const newDebris = {
      id: Date.now() + Math.random(),
      x: Math.random() * gameWidth,
      y: -20,
      speed: 1 + Math.random() * 3,
      size: 5 + Math.random() * 10, // Smaller debris (5-15px instead of 10-30px)
    };

    setDebris((prev) => [...prev, newDebris]);

    const delay = 800 + Math.random() * 1500;
    setTimeout(generateDebris, delay);
  }, [gameActive, debris.length]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!gameActive) return;
      
      const moveSpeed = 15;
      const newPosition = { ...shipPosition };

      if (e.key === 'ArrowLeft') newPosition.x = Math.max(15, newPosition.x - moveSpeed);
      if (e.key === 'ArrowRight') newPosition.x = Math.min(gameWidth - 15, newPosition.x + moveSpeed);
      if (e.key === 'ArrowUp') newPosition.y = Math.max(15, newPosition.y - moveSpeed);
      if (e.key === 'ArrowDown') newPosition.y = Math.min(gameHeight - 15, newPosition.y + moveSpeed);

      setShipPosition(newPosition);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameActive, shipPosition]);

  // Move debris and check collisions
  useEffect(() => {
    if (!gameActive) return;

    const gameLoop = setInterval(() => {
      setDebris((prevDebris) => {
        return prevDebris
          .map((piece) => ({
            ...piece,
            y: piece.y + piece.speed,
          }))
          .filter((piece) => {
            // Check collision with ship
            const distance = Math.sqrt(
              Math.pow(piece.x - shipPosition.x, 2) + 
              Math.pow(piece.y - shipPosition.y, 2)
            );

            if (distance < piece.size / 2 + 15) {
              setEnergy((e) => e - 10);
              return false;
            }

            return piece.y < gameHeight + 20;
          });
      });

      // Slowly recharge energy if no collision
      if (energy < 100) {
        setEnergy((e) => Math.min(e + 0.1, 100));
      }

      // Game over condition
      if (energy <= 0) {
        setGameActive(false);
        setMessage("Your ship was destroyed! Try again.");
      }
    }, 16);

    return () => clearInterval(gameLoop);
  }, [gameActive, debris, shipPosition, energy]);

  // Start debris generation
  useEffect(() => {
    if (gameActive) {
      generateDebris();
    }
  }, [gameActive, generateDebris]);

  const resetGame = () => {
    setDebris([]);
    setEnergy(100);
    setShipPosition({ x: 150, y: 150 });
    setGameActive(true);
    setMessage("Navigate through space debris!");
  };

  return (
    <div className="space-explorer-game">
      <p>{message}</p>
      
      <div className="game-container" style={{ width: `${gameWidth}px`, height: `300px` }}>
        <div className="game-area">
          {debris.map((piece) => (
            <div
              key={piece.id}
              className="debris"
              style={{
                left: `${piece.x}px`,
                top: `${piece.y}px`,
                width: `${piece.size}px`,
                height: `${piece.size}px`,
                opacity: 0.8,
              }}
            />
          ))}
          <div
            className="spaceship"
            style={{
              left: `${shipPosition.x}px`,
              top: `${shipPosition.y}px`,
            }}
          >
            <FaRocket className="rocket-icon" />
          </div>
        </div>
        
        <div className="game-info">
          <div className="energy-bar">
            <div 
              className="energy-fill" 
              style={{ width: `${energy}%` }}
            />
          </div>
          <span>Energy: {Math.floor(energy)}%</span>
          
          {!gameActive && (
            <button onClick={resetGame}>Launch Again</button>
          )}
        </div>
      </div>
    </div>
  );
};

const ErrorPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="error-page-container"
    >
      <div className="error-card">
        <div className="error-card-header">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <FaExclamationTriangle className="error-icon" />
          </motion.div>
        </div>
        
        <div className="error-card-body">
          <h1 className="error-title">404</h1>
          <h2 className="error-subtitle">Page Not Found</h2>
          <p className="error-message">
            You've drifted to cosmic void. <br />
            The page you're seeking is lost among the stars.
          </p>
          
         
          
          <div className="game-section">
            <SpaceExplorerGame />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ErrorPage;